"use client";

import React, { useState, useEffect } from "react";
import { Search, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-4 w-16 animate-pulse bg-muted/20 rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} style={{
            fontSize: "1.3rem"
          }} className="font-default text-base font-medium tracking-tight text-foreground/90 hover:text-primary transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary hover:bg-transparent">
          <Menu className="size-6" />
          <span className="sr-only">Open menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="bg-background text-foreground border-r-border/10 w-[300px] sm:w-[400px] pt-16">
        <div className="flex flex-col gap-8">
          {loading ? <div className="space-y-6">
              {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-32 animate-pulse bg-muted/20 rounded-md" />)}
            </div> : <nav className="flex flex-col gap-6">
              {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={() => setIsOpen(false)} className="font-default text-lg font-semibold tracking-tight text-foreground/80 hover:text-primary transition-colors w-fit">
                  {item.label}
                </Link>)}
            </nav>}
        </div>
      </SheetContent>
    </Sheet>;
}
function HeaderSearch() {
  const [searchValue, setSearchValue] = useState("");
  return <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="text-foreground hover:text-primary hover:bg-transparent transition-colors">
          <Search className="size-5" />
          <span className="sr-only">Search properties</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-4 bg-background border-border/20 shadow-xl rounded-lg">
        <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search cities in TX or LA..." className="w-full" />
      </PopoverContent>
    </Popover>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="264750" id="header" className={`bg-background text-foreground font-default sticky top-0 z-50 w-full transition-all duration-500 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border/10 shadow-sm" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-4 flex items-center justify-between !h-[100px] md:px-8">
        
        {}
        <MenuProvider menu_id="39" className="flex-1 flex items-center justify-start">
          <MobileMenu />
          <DesktopMenu />
        </MenuProvider>

        {}
        <div className="flex-1 flex !justify-center !items-start !h-[100px] ![margin:0px_100px_0px_100px]">
          <Link to="/" className="flex transition-opacity !items-center !justify-center hover:opacity-80">
            <WvcLogo className="text-primary !w-[155px] ![margin:10px_0px_32px_0px] [&_svg]:text-primary [&_svg]:!w-[155px] [&_svg]:![margin:10px_0px_32px_0px]" />
          </Link>
        </div>

        {}
        <div className="flex-1 flex items-center justify-end gap-1 md:gap-4">
          <HeaderSearch />
          <Button asChild className="hidden md:flex bg-gradient-to-r from-primary to-secondary text-primary-foreground font-semibold rounded-md shadow-md hover:shadow-lg transition-all">
            <Link to="/ListYourPropertyPage">List Your Property</Link>
          </Button>
        </div>

      </div>
    </section>;
}